import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import CodePicker from '../coding/CodePicker';
import ConfirmDialog from '../coding/ConfirmDialog';
import { utCodesFor, rcCodes } from '../coding/codes';
import { lockCode } from '../../services/adjudicationService';
import useCodingKeys from './useCodingKeys';
import { DIMENSION_NAMES, KeysHint, Panel } from './parts';

const SPEAKER_HINT = {
    teacher: 'Teacher codes only: the utterance is a teacher’s',
    student: 'Student codes only: the utterance is a student’s',
    unknown: 'All codes: the speaker is unknown',
};

// Blind coding (wf-adj-item-blind 1, 3): UT for the target's speaker and RC, nothing pre-selected,
// an unsure mark that does not change the code, and a confirm step before the lock. Before the lock
// nothing about the item is shown: no earlier label, no model output, no reason it was chosen.
const BlindStep = ({ runId, view, codebook, onPicksChange, onLocked, onMovedOn }) => {
    const [ut, setUt] = useState(null);
    const [rc, setRc] = useState(null);
    const [unsure, setUnsure] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const speaker = view.item.speaker;
    const utList = utCodesFor(codebook, speaker);
    const rcList = rcCodes(codebook);
    const ready = Boolean(ut && rc);

    useEffect(() => {
        onPicksChange?.({ ut: ut ? [ut] : [], rc: rc ? [rc] : [] });
    }, [ut, rc, onPicksChange]);

    useCodingKeys({
        enabled: !confirming,
        utCodes: utList.map((e) => e.code),
        rcCodes: rcList.map((e) => e.code),
        onUt: setUt,
        onRc: setRc,
        onUnsure: () => setUnsure((u) => !u),
        onEnter: () => ready && setConfirming(true),
    });

    const lock = async () => {
        setBusy(true);
        setError(null);
        try {
            onLocked(await lockCode(runId, view.item.id, { ut, rc, unsure }));
        } catch (err) {
            setBusy(false);
            if (err.response?.status === 409) {
                setConfirming(false);
                onMovedOn();
            } else {
                setError(err.response?.status === 400 ? 'That code isn’t allowed here. Pick again.' : 'Couldn’t lock your code. Try again.');
            }
        }
    };

    return (
        <Panel title="Your code">
            <Box sx={{ display: 'grid', gap: 2.5 }}>
                <CodePicker id="pick-ut" stacked label={DIMENSION_NAMES.ut} codes={utList} value={ut} onChange={setUt} hint={SPEAKER_HINT[speaker] || SPEAKER_HINT.unknown} />
                <CodePicker id="pick-rc" stacked label={DIMENSION_NAMES.rc} codes={rcList} value={rc} onChange={setRc} />
            </Box>

            <FormControlLabel
                sx={{ mt: 2, alignItems: 'flex-start', ml: -1 }}
                control={<Checkbox checked={unsure} onChange={(e) => setUnsure(e.target.checked)} sx={{ pt: 0.25 }} />}
                label={(
                    <Box>
                        <Typography variant="body2">I’m unsure about this code</Typography>
                        <Typography variant="caption" color="text.secondary">Recorded with your code; it does not change it.</Typography>
                    </Box>
                )}
            />

            <Button variant="contained" size="large" fullWidth disabled={!ready} onClick={() => setConfirming(true)} sx={{ mt: 2.5, minHeight: 46 }}>
                Lock code
            </Button>
            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.25 }}>
                After you lock you see the earlier label. A locked code can’t be changed.
            </Typography>
            <KeysHint>letter for UT · 1–5 for RC · U for unsure · Enter to lock</KeysHint>

            <ConfirmDialog
                open={confirming}
                title="Lock your code?"
                confirmLabel="Lock code"
                cancelLabel="Back"
                busy={busy}
                error={error}
                onConfirm={lock}
                onClose={() => setConfirming(false)}
            >
                <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 1 }}>
                    UT {ut} · RC {rc} · {unsure ? 'unsure' : 'not unsure'}
                </Typography>
                <Typography variant="body2">
                    After you lock, you’ll see the earlier label for this utterance and give a reason where yours differs. Your code can’t be changed.
                </Typography>
            </ConfirmDialog>
            {error && !confirming && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Panel>
    );
};

export default BlindStep;
